import { deleteObject, getDownloadURL, listAll, ref, uploadBytesResumable, type StorageReference } from 'firebase/storage';
import { storage } from '../firebase';
import type { AssetRef } from '@flashpoint/site-dna';
import { optimizeImageForSlot, type AssetSlot } from './optimizeImage';

export interface UploadProgress {
  bytesTransferred: number;
  totalBytes: number;
  percent: number;
}

export interface UploadHandle {
  promise: Promise<AssetRef>;
  cancel: () => void;
}

function safeName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]+/g, '_');
}

/** Browsers often leave `file.type` empty (e.g. some HEIC); Storage rules require `image/*` for this path. */
function contentTypeForUpload(file: File, fallbackMime?: string): string {
  if (file.type) return file.type;
  if (fallbackMime) return fallbackMime;
  const n = file.name.toLowerCase();
  if (n.endsWith('.png')) return 'image/png';
  if (n.endsWith('.jpg') || n.endsWith('.jpeg')) return 'image/jpeg';
  if (n.endsWith('.webp')) return 'image/webp';
  if (n.endsWith('.gif')) return 'image/gif';
  if (n.endsWith('.svg')) return 'image/svg+xml';
  if (n.endsWith('.avif')) return 'image/avif';
  if (n.endsWith('.heic') || n.endsWith('.heif')) return 'image/heic';
  if (n.endsWith('.bmp')) return 'image/bmp';
  if (n.endsWith('.tif') || n.endsWith('.tiff')) return 'image/tiff';
  return 'image/jpeg';
}

/**
 * Resumable upload of a single file to leads/{leadId}/assets/{filename} in
 * the internal Firebase Storage bucket. Images are resized/compressed for
 * demo use before upload (SVG passes through; HEIC rejected).
 */
export function uploadLeadAsset(
  leadId: string,
  file: File,
  opts: {
    slot?: AssetSlot;
    onProgress?: (p: UploadProgress) => void;
    width?: number;
    height?: number;
    alt?: string;
  } = {},
): UploadHandle {
  let cancelled = false;
  let task: ReturnType<typeof uploadBytesResumable> | null = null;

  const promise = (async (): Promise<AssetRef> => {
    const slot = opts.slot ?? 'gallery';
    const optimized = await optimizeImageForSlot(file, slot);
    if (cancelled) {
      throw new Error('Upload cancelled');
    }

    const uploadFile = optimized.file;
    const mimeType = contentTypeForUpload(uploadFile, optimized.mimeType);
    const filename = `${Date.now()}_${safeName(uploadFile.name)}`;
    const path = `leads/${leadId}/assets/${opts.slot ? `${opts.slot}/` : ''}${filename}`;
    const storageRef = ref(storage, path);

    task = uploadBytesResumable(storageRef, uploadFile, {
      contentType: mimeType,
      customMetadata: { originalName: file.name },
    });

    return await new Promise<AssetRef>((resolve, reject) => {
      task!.on(
        'state_changed',
        (snap) => {
          opts.onProgress?.({
            bytesTransferred: snap.bytesTransferred,
            totalBytes: snap.totalBytes,
            percent: snap.totalBytes > 0 ? snap.bytesTransferred / snap.totalBytes : 0,
          });
        },
        (err) => {
          reject(err);
        },
        async () => {
          try {
            if (cancelled) {
              reject(new Error('Upload cancelled'));
              return;
            }
            const downloadURL = await getDownloadURL(task!.snapshot.ref);
            const width = opts.width ?? optimized.width;
            const height = opts.height ?? optimized.height;
            resolve({
              storagePath: path,
              downloadURL,
              mimeType,
              bytes: uploadFile.size,
              ...(width != null && width > 0 ? { width } : {}),
              ...(height != null && height > 0 ? { height } : {}),
              ...(opts.alt != null && opts.alt !== '' ? { alt: opts.alt } : {}),
            });
          } catch (err) {
            reject(err);
          }
        },
      );
    });
  })();

  return {
    promise,
    cancel: () => {
      if (!cancelled) {
        cancelled = true;
        task?.cancel();
      }
    },
  };
}

async function deleteFolderRecursive(folder: StorageReference): Promise<void> {
  const listing = await listAll(folder);
  await Promise.all([
    ...listing.items.map((item) => deleteObject(item)),
    ...listing.prefixes.map((prefix) => deleteFolderRecursive(prefix)),
  ]);
}

/** Delete every object under `leads/{leadId}/assets/**`. Missing prefix is a no-op. */
export async function deleteLeadAssets(leadId: string): Promise<void> {
  try {
    await deleteFolderRecursive(ref(storage, `leads/${leadId}/assets`));
  } catch (err) {
    const code = err && typeof err === 'object' && 'code' in err ? String((err as { code: unknown }).code) : '';
    if (code === 'storage/object-not-found') return;
    throw err;
  }
}
